import type { Text as YText } from 'yjs';

import type { DatabaseCrdtValue } from './database-writer-kernel';
import { stringifyDatabaseCrdt } from './database-writer-wire';

/** Kernel bytes are plain values here, as Yjs returns them (`Uint8Array`). */
export type SerializedValue =
  | { kind: 'map'; entries: ReadonlyArray<readonly [string, SerializedValue]> }
  | { kind: 'array'; items: readonly SerializedValue[] }
  | { kind: 'text'; delta: ReturnType<YText['toDelta']> }
  | { kind: 'value'; value: unknown };
export type PathPart = string | { key: string; value: string };
export interface DatabaseSemanticChange {
  readonly docId: string;
  readonly collection: string;
  readonly path: readonly PathPart[];
  readonly before: SerializedValue;
  readonly after: SerializedValue;
}
export type DatabaseSemanticSnapshot = ReadonlyMap<string, SerializedValue>;

const absent: SerializedValue = { kind: 'value', value: undefined };
/** History form of a detached kernel value. */
export function databaseSemanticValue(
  value: DatabaseCrdtValue
): SerializedValue {
  switch (value.kind) {
    case 'absent':
      return absent;
    case 'bytes':
      return { kind: 'value', value: value.value };
    case 'map':
      return {
        kind: 'map',
        entries: value.entries.map(([k, v]) => [k, databaseSemanticValue(v)]),
      };
    case 'array':
      return { kind: 'array', items: value.items.map(databaseSemanticValue) };
    default:
      return value;
  }
}
export function equal(a: SerializedValue, b: SerializedValue): boolean {
  return stringifyDatabaseCrdt(a) === stringifyDatabaseCrdt(b);
}
/** Selector of a keyed entry, read from its history or its kernel value. */
export function identity(
  value: SerializedValue | DatabaseCrdtValue
): PathPart | undefined {
  if (value.kind === 'map') {
    const entries = new Map<string, SerializedValue | DatabaseCrdtValue>(
      value.entries
    );
    for (const key of ['shardId', 'partId', 'id', 'columnId']) {
      const id = entries.get(key);
      if (id?.kind === 'value' && typeof id.value === 'string')
        return { key, value: id.value };
    }
  }
  if (value.kind === 'value') {
    if (typeof value.value === 'string') return value.value;
    if (
      value.value &&
      typeof value.value === 'object' &&
      'id' in value.value &&
      typeof value.value.id === 'string'
    )
      return { key: 'id', value: value.value.id };
  }
  return undefined;
}
export function identityKey(value: SerializedValue): string {
  return stringifyDatabaseCrdt(identity(value) ?? value);
}

export function diffDatabaseSemantics(
  docId: string,
  before: DatabaseSemanticSnapshot,
  after: DatabaseSemanticSnapshot
): DatabaseSemanticChange[] {
  const changes: DatabaseSemanticChange[] = [];
  const visit = (
    collection: string,
    path: readonly PathPart[],
    old: SerializedValue,
    next: SerializedValue
  ) => {
    if (equal(old, next)) return;
    if (old.kind === 'map' && next.kind === 'map') {
      const a = new Map(old.entries),
        b = new Map(next.entries);
      for (const key of new Set([...a.keys(), ...b.keys()]))
        visit(
          collection,
          [...path, key],
          a.get(key) ?? absent,
          b.get(key) ?? absent
        );
      return;
    }
    if (old.kind === 'array' && next.kind === 'array') {
      const a = new Map(old.items.map(item => [identityKey(item), item]));
      const b = new Map(next.items.map(item => [identityKey(item), item]));
      if (
        a.size === old.items.length &&
        b.size === next.items.length &&
        old.items.every(identity) &&
        next.items.every(identity)
      ) {
        for (const [key, item] of a) {
          const target = b.get(key),
            selector = identity(item);
          if (target && typeof selector === 'object')
            visit(collection, [...path, selector], item, target);
        }
        const order = old.items.map(identityKey);
        const nextOrder = next.items.map(identityKey);
        if (JSON.stringify(order) === JSON.stringify(nextOrder)) return;
        // Entries appended behind the unchanged ones are recorded one by one,
        // so the history of a growing list does not repeat the list.
        const appended = next.items.slice(old.items.length);
        if (
          JSON.stringify(nextOrder.slice(0, order.length)) ===
            JSON.stringify(order) &&
          appended.every(item => typeof identity(item) === 'object')
        ) {
          for (const item of appended)
            changes.push({
              docId,
              collection,
              path: [...path, identity(item)!],
              before: absent,
              after: item,
            });
          return;
        }
      }
    }
    changes.push({ docId, collection, path, before: old, after: next });
  };
  for (const name of new Set([...before.keys(), ...after.keys()])) {
    // A top-level Yjs type survives undo; an absent collection means its empty value.
    const old = before.get(name),
      next = after.get(name);
    const empty =
      (old ?? next)?.kind === 'array'
        ? { kind: 'array' as const, items: [] }
        : { kind: 'map' as const, entries: [] };
    visit(name, [], old ?? empty, next ?? empty);
  }
  return changes;
}
